//  ----------      START   IMPORT  BLOCK       ----------
import { useState, useEffect } from 'react';
import { useSearchParams } from "react-router-dom";
//  Widgets
import Sidebar from '../SideBar/SideBar';
import TopBar from '../TopBar/TopBar';
import ContentArea from '../ContentArea/ContentArea';
import { Container, Row, Col, Offcanvas } from 'react-bootstrap';
//  Context
import { useApplicationUI } from '../../API/Context/AppUIContext';
import { ContentNames, GeneralContentNames } from '../../ConfigFiles/content_names/content_names';
import { useUser } from '../../API/Context/UserContext';
import { useActivePatient } from '../../API/Hooks/ActivePatientContext';
//  Config
import './Dashboard.css';
import HISTORY_STATUS from '../../ConfigFiles/statuses/history_status_constants';
//  ----------      END   IMPORT  BLOCK       ----------

/**
 * Central component responsible for the Dashboard, it merges Topbar, Sidebar and ContentArea.
 * It coordinates the correct active item between the Sidebar and ContentArea
 * and then renders the component specified by the activeItem.
 */
const Dashboard = () => {
  //  Context
  const { user, isPatientProfileComplete } = useUser();
  const { isProfessionalDashboardActive } = useApplicationUI();
  const { loadSelectedPatient, resetToSelf, isSelfViewing, activePatient } = useActivePatient();

  // Read URL Query Parameters (e.g. /dashboard?tab=privacy&add_user=dr_smith)
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const initialAddUser = searchParams.get("add_user") || undefined;

  // Determine initial active item (Query Param > Dashboard Preference default)
  const [activeItem, setActiveItem] = useState<string>(() => {
    if (tabParam) return tabParam;
    return isProfessionalDashboardActive ? ContentNames.SUMMARY : GeneralContentNames.SUMMARY;
  });


  const [showMobileSidebar, setShowMobileSidebar] = useState(false);

  useEffect(() => {
    if (!user) return;

    if (isProfessionalDashboardActive) {
      // MODE: Professional -> If viewing self, reset/clear active patient selection
      if (isSelfViewing && activePatient) {
        resetToSelf(); // or clear active patient
      }
    } else {
      // MODE: Patient View -> Ensure we are loaded on 'me'
      if (!isSelfViewing || !activePatient) {
        if (isPatientProfileComplete) {
          loadSelectedPatient('me');
        }
      }
    }
  }, [isProfessionalDashboardActive, user, isSelfViewing, activePatient, isPatientProfileComplete, loadSelectedPatient, resetToSelf]);

  const handleClose = () => setShowMobileSidebar(false);
  const handleShow = () => setShowMobileSidebar(true);

  const handleSidebarSelect = (item: string) => {
    setActiveItem(item);
    handleClose();
  };

  return (
    <div className="dashboard-wrapper">
      <TopBar onToggleSidebar={handleShow} />
      <Container fluid className="p-0">
        <Row className="g-0 flex-nowrap">
          {/* DESKTOP SIDEBAR */}
          <Col
            md={3}
            lg={2}
            className="d-none d-md-block border-right"
            id="sidebar-wrapper"
          >
            <Sidebar onSelect={setActiveItem} activeItem={activeItem}/>
          </Col>

          {/* 2. MAIN CONTENT - Ensures it takes remaining space */}
          <Col
            xs={12}
            md={9}
            lg={10}
            id="page-content-wrapper"
            className="flex-grow-1"
          >
            <ContentArea activeItem={activeItem} initialAddUser={initialAddUser}/>
          </Col>
        </Row>
      </Container>

      {/* 3. MOBILE OFFCANVAS */}
      <Offcanvas
        show={showMobileSidebar}
        onHide={handleClose}
        placement="start"
        className="d-md-none bg-light"
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>MedCleir</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body className="p-0">
          <Sidebar onSelect={handleSidebarSelect} activeItem={activeItem} />
        </Offcanvas.Body>
      </Offcanvas>
    </div>
  );
};

export default Dashboard;